import { Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { beginLogin, isHiveMode } from '@/lib/hive';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Hive mode: /login is a stale bookmark — hand off to Hive sign-in.
  useEffect(() => {
    if (isHiveMode()) void beginLogin('/app');
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (signInError) setError(signInError.message);
    else navigate({ to: '/app' });
  }

  if (isHiveMode()) {
    return (
      <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-6">
        <Card className="w-full">
          <h1 className="text-2xl font-semibold">Redirecting to sign-in…</h1>
          <p className="mt-2 text-sm text-slate-500">
            Athena uses Hive single sign-on.{' '}
            <button
              type="button"
              onClick={() => void beginLogin('/app')}
              className="text-[var(--color-accent)] underline-offset-4 hover:underline"
            >
              Continue to Hive
            </button>{' '}
            if you aren't redirected automatically.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-6">
      <Card className="w-full">
        <h1 className="text-2xl font-semibold">Log in to Athena</h1>
        <p className="mt-1 text-sm text-slate-500">
          Don't have an account? <Link to="/signup" className="text-[var(--color-accent)] underline-offset-4 hover:underline">Sign up</Link>
        </p>

        {!isSupabaseConfigured && (
          <div className="mt-4 rounded-md border border-[var(--color-warning)] bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">
            Supabase not configured. Set <code>VITE_SUPABASE_URL</code> and <code>VITE_SUPABASE_ANON_KEY</code> in the repo-root <code>.env</code>, then restart the dev server.
          </div>
        )}

        <form onSubmit={onSubmit} className="mt-6 space-y-4" aria-busy={submitting}>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Password</Label>
              <Link to="/reset-password" className="text-xs text-slate-500 hover:text-slate-900 dark:hover:text-slate-100">
                Forgot?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <div aria-live="polite" className="text-sm text-[var(--color-critical)]">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={submitting || !isSupabaseConfigured}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
